import { useState, useEffect, useRef } from "react";
import { Button, Input, Text, VStack } from "@chakra-ui/react";
import { Dialog } from "@chakra-ui/react/dialog";

interface RenameCollectionModalProps {
  open: boolean;
  currentTitle: string;
  isLoading: boolean;
  onConfirm: (newTitle: string) => void;
  onClose: () => void;
}

export default function RenameCollectionModal({
  open,
  currentTitle,
  isLoading,
  onConfirm,
  onClose,
}: RenameCollectionModalProps) {
  const [value, setValue] = useState(currentTitle);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync value when modal opens with a (possibly updated) title
  useEffect(() => {
    if (open) {
      setValue(currentTitle);
      // Focus + select all after the dialog has rendered
      setTimeout(() => inputRef.current?.select(), 50);
    }
  }, [open, currentTitle]);

  const handleConfirm = () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== currentTitle) {
      onConfirm(trimmed);
    } else {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleConfirm();
    if (e.key === "Escape") onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(d) => { if (!d.open) onClose(); }} size="sm">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          bg="bg.surface"
          border="1px solid"
          borderColor="border.muted"
          borderRadius="xl"
          shadow="xl"
          mx={{ base: 4, sm: 0 }}
        >
          <Dialog.Header borderBottomWidth="1px" borderColor="border.muted" pb={4}>
            <Text fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary">
              Rename collection
            </Text>
          </Dialog.Header>

          <Dialog.Body pt={5} pb={2}>
            <VStack gap={4} align="stretch">
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Collection name"
                fontFamily="body"
                fontWeight="500"
                fontSize="sm"
                color="text.primary"
                borderColor="border.muted"
                _focus={{ borderColor: "accent.secondary", boxShadow: "none" }}
              />
            </VStack>
          </Dialog.Body>

          <Dialog.Footer pt={4} pb={5} gap={3}>
            <Button
              variant="ghost"
              fontFamily="body"
              fontWeight="500"
              fontSize="sm"
              color="text.muted"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              bg="accent.emphasis"
              color="white"
              fontFamily="body"
              fontWeight="500"
              fontSize="sm"
              _hover={{ bg: "accent.emphasisHover" }}
              onClick={handleConfirm}
              loading={isLoading}
              disabled={!value.trim() || value.trim() === currentTitle}
            >
              Save
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
